import { useEffect } from 'react';
import Navbar from './components/Navbar';
import GangaIntro from './components/GangaIntro';
import AboutProject from './components/AboutProject';
import DataReveals from './components/DataReveals';
import ThesisPreview from './components/ThesisPreview';
import ProjectVideoExperience from './components/ProjectVideoExperience';
import DocumentarySection from './components/DocumentarySection';
import IITGallery from './components/IITGallery';
import AboutTeam from './components/AboutTeam';
import Footer from './components/Footer';

export default function HomePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemas = [
      {
        id: 'schema-website',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Echoes of the Ganga',
          url: siteUrl,
          description:
            'A data-driven environmental analytics project analyzing Ganga River pollution patterns using machine learning and data science. Presented at IIT Roorkee.',
          inLanguage: 'en',
          publisher: {
            '@type': 'Organization',
            name: 'Echoes of the Ganga Research Team',
            url: siteUrl,
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${siteUrl}/?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
      },
      {
        id: 'schema-research',
        data: {
          '@context': 'https://schema.org',
          '@type': 'ResearchProject',
          name: 'Echoes of the Ganga',
          url: siteUrl,
          description:
            'Comprehensive environmental analytics research using data science and machine learning to analyze Ganga River pollution patterns, identify hotspots, and propose sustainable river management solutions.',
          keywords:
            'Ganga River pollution, environmental data science, water quality analysis, IIT Roorkee research, machine learning, river pollution trends, sustainable river management',
          image:
            'https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/7fa6bda7fb8710b30e71d93f5a52549a.png',
          dateCreated: '2024-01-01',
          dateModified: '2026-04-05',
          member: [
            {
              '@type': 'Person',
              name: 'Sarthak Bobade',
              jobTitle: 'Data Analyst & Developer',
            },
            {
              '@type': 'Person',
              name: 'Pavan Khandare',
              jobTitle: 'Data Analyst & Developer',
            },
            {
              '@type': 'Person',
              name: 'Dr. Rachana Chavan',
              jobTitle: 'Research Advisor | Environmental Science',
            },
          ],
          funder: {
            '@type': 'EducationalOrganization',
            name: 'IIT Roorkee',
            url: 'https://www.iitr.ac.in',
          },
        },
      },
      {
        id: 'schema-webpage',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: 'Echoes of the Ganga | Data-Driven Environmental Analytics | IIT Roorkee Research',
          description:
            'Echoes of the Ganga is a comprehensive environmental analytics project using data science and machine learning to analyze Ganga River pollution patterns. Presented at IIT Roorkee workshop.',
          inLanguage: 'en',
          dateModified: '2026-04-05',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: {
            '@type': 'Thing',
            name: 'Ganga River Pollution Analytics',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
            ],
          },
        },
      },
      {
        id: 'schema-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is Echoes of the Ganga?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Echoes of the Ganga is a data-driven environmental analytics research project that uses machine learning and data science to analyze Ganga River pollution patterns, identify pollution hotspots, and propose sustainable river management solutions.',
              },
            },
            {
              '@type': 'Question',
              name: 'What methods were used to analyze Ganga River pollution?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The research employed advanced data science techniques including machine learning models, statistical analysis, and data visualization to process water quality datasets and identify pollution trends across different stretches of the Ganga River.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is this research affiliated with IIT Roorkee?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, the Echoes of the Ganga project was presented at an IIT Roorkee workshop and is supported by academic guidance from Dr. Rachana Chavan, Research Advisor in Environmental Science.',
              },
            },
            {
              '@type': 'Question',
              name: 'What are the key findings of the Ganga pollution research?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The research identified major pollution hotspots along the Ganga River, analyzed seasonal pollution trends, and provided data-driven insights into industrial and agricultural contributions to water quality degradation, supporting evidence-based conservation strategies.',
              },
            },
          ],
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navbar />
      {/* Section 1: Ganga Introduction */}
      <GangaIntro />
      {/* Section 2: About the Project */}
      <AboutProject />
      {/* Section 3: What We Found */}
      <DataReveals />
      {/* Section 4: Thesis Preview */}
      <ThesisPreview />
      {/* Section 4.5: Project Video Experience */}
      <ProjectVideoExperience />
      {/* Section 5: Documentary */}
      <DocumentarySection />
      {/* Section 6: IIT Roorkee Photos */}
      <IITGallery />
      {/* Section 7: About the Team */}
      <AboutTeam />
      {/* Footer */}
      <Footer />
    </div>
  );
}
